import * as React from 'react';
import { useState, useMemo, useCallback, useEffect } from 'react';
import * as R from 'ramda';
import { useDispatch } from 'react-redux';
import LoginLayout from '../login/LoginLayout';
import { securityQuestionAction } from './api/securityQuestionAction';
import ForgotPasswordEmailForm from './ForgotPasswordEmailForm';
import { SecurityQuestionKey } from '../securityQuestion/models';
import { ActionType } from '../../core/redux/actionType';
import ForgotPasswordSecurityAnswerForm from './ForgotPasswordSecurityAnswerForm';
import { FLEX_ROW } from '../../layout/flexBox';
import { css } from '@emotion/core';
import { Str } from '../../localization/translationKeys';
import { useLoading, useError } from '../../core/api/hooks';
import { useMsgText } from '../../localization/hooks';
import ForgotPasswordSuccessForm from './ForgotPasswordSuccessForm';
import { useMount } from '../../core/hooks/useMount';
import { securityQuestionKeysAction } from './api/securityQuestionKeysAction';
import Msg from '../../localization/Msg';
import { useToggleOnSuccess } from '../../core/hooks/useToggleOnSuccess';
import { useAppState } from '../../core/redux/hooks';
import { parseQueryToKeyedStrings } from '../../navigation/module';
import { setLocaleAction } from '../../localization/setLocaleAction';
import { Language } from '../../localization/models';
import { TextDirection } from '../../localization/textDirection';
import { useLocation } from 'react-router-dom';


const styles = {
  header: [FLEX_ROW, css`min-height: 35px; font-size: 18px; font-weight: bold;`],
};


type Props = {
  className?: string;
  loginUrl: string;
  requestActionType: ActionType;
  requestPasswordResetEmail: (email: string, languageId: number) => void;
};

type UserQueryObject = {
  langid?: number;
};

/**
 * ForgotPasswordPage: a component that allows users to request a reset link for their TruScore account password by
 * submitting their account email address to retrieve the security question they submitted on account creation and
 * answering their security question correctly.
 *
 * Upon successfully answering their security question, users are notified that an email containing the reset link has
 * been sent.
 *
 * PureComponent: true
 * Connected to Redux: true
 */
const ForgotPasswordPage: React.FC<Props> = props => {
  const [emailAddress, setEmailAddress] = useState('');
  const dispatch                        = useDispatch();

  useMount(() => { dispatch(securityQuestionKeysAction); });

  const pwResetInfo          = useAppState(state => state.auth.passwordReset);
  const securityQuestionKeys = useAppState(state => state.auth.securityQuestionKeys ?? []);
  const stateLangId          = useAppState(state => state.locale.lang.languageId);

  // check if there is a langid on the query string
  const location = useLocation();
  const userQueryObj = parseQueryToKeyedStrings<UserQueryObject>(location.search);
  const allLangs = useAppState(state => state.locale.portalLanguages ?? {});

  const paramLangId = userQueryObj?.langid;

  useEffect(
    () => {
      if (paramLangId) {
        const language = Object.values(allLangs).find((lang: Language) => lang.languageId === +paramLangId);

        if (language && language.languageId > 0) {
            dispatch(setLocaleAction(language, language.rtl ? TextDirection.RTL : TextDirection.LTR));
        }
      }
    },
    [allLangs, dispatch, paramLangId],
  );

  const languageId = paramLangId ?? stateLangId;

  const getSecurityQuestion = useCallback(
    (emailAddress: string) => dispatch(securityQuestionAction(emailAddress)),
    [dispatch],
  );

  const sendPasswordResetEmailRequest = useCallback(
    (email: string) => dispatch(props.requestPasswordResetEmail(email, languageId)),
    [dispatch, languageId, props],
  );

  const awaitingSecurityQuestion = useLoading(ActionType.SecurityQuestionRequest);
  const emailAddressNotFound     = !!useError(ActionType.SecurityQuestionRequest);

  const resetRequestMetaTag = useMemo(() => `${emailAddress}-${languageId}`, [emailAddress, languageId]);

  const awaitingEmailRequestResult = useLoading(props.requestActionType, resetRequestMetaTag);
  const errorRequestingEmail       = !!useError(props.requestActionType, resetRequestMetaTag);

  const [requestSubmitted] = useToggleOnSuccess(props.requestActionType, resetRequestMetaTag);

  const securityQuestionKey = useMemo(
    () => R.find<SecurityQuestionKey>(
      R.propEq<'questionId', number | undefined>('questionId', pwResetInfo?.securityQuestionId),
      securityQuestionKeys,
    ),
    [pwResetInfo, securityQuestionKeys],
  );

  const securityQuestion = useMsgText(securityQuestionKey?.key || '');

  const enteringEmail             = !requestSubmitted && !securityQuestion;
  const answeringSecurityQuestion = !requestSubmitted && !!securityQuestion;
  const forgotPasswordSuccess     = !answeringSecurityQuestion && !enteringEmail;

  return (
    <LoginLayout isLoading={awaitingSecurityQuestion || awaitingEmailRequestResult} className={props.className}>
      <Msg css={styles.header} str={Str.ForgotPassword} />
      {
        enteringEmail && (
          <ForgotPasswordEmailForm
            getSecurityQuestion={getSecurityQuestion}
            setEmailAddress={setEmailAddress}
            emailAddressNotFound={emailAddressNotFound}
            isLoading={awaitingSecurityQuestion}
          />
        )
      }
      {
        answeringSecurityQuestion && (
          <ForgotPasswordSecurityAnswerForm
            sendPasswordResetEmailRequest={sendPasswordResetEmailRequest}
            emailAddress={emailAddress}
            question={securityQuestion}
            answer={pwResetInfo?.securityAnswer!}
            incorrectSecurityAnswer={errorRequestingEmail}
            isLoading={awaitingEmailRequestResult}
          />
        )
      }
      {
        forgotPasswordSuccess && (
          <ForgotPasswordSuccessForm loginUrl={props.loginUrl} />
        )
      }
    </LoginLayout>
  );
};

export default ForgotPasswordPage;
